import { useRef, useState } from "react"
import Comm from "../../Comm"
import PageTitle from "../../components/PageTitle"
import WhiteSpace from "../../components/Whitespace"
import ChargerType from "../../types/ChargerType"
import { useTranslation } from "react-i18next"
import React from "react"
import LoaderDots from "../../components/LoaderDots"
import InputWithLabel from "../../components/InputWithLabel"
import Button from "../../components/Button"
import Icon from "../../Icon"
import Dropdown from "../../components/Dropdown"
import styled from "styled-components"
import { BREAKPOINTS } from "../../GlobalTheme"

/* Every control on this page shares one column width, so the stack has a single left and right edge.
   Previously ControlsRow, the Get files button and the file list each carried their own width. */
const FormColumn = styled.div`
  @media (max-width: ${BREAKPOINTS.shell}px) {
    max-width: min(max(30%, 288px), 100%);

    > * {
      width: 100% !important;
      box-sizing: border-box;
    }
  }
`

const ControlsRow = styled.div`
  display: flex;

  /* Wrapping left each control at a different width and a different right edge, with dead space
     beside them. One column, one width, one edge. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    /* Path, Filter and Reset all carry explicit width props sized for a desktop row, and the inputs
       inside the groups carry their own - all of them have to give way for the column to have one
       edge. */
    > *,
    > * > *:first-child,
    > * > *:first-child > * {
      width: 100% !important;
      box-sizing: border-box;
    }
  }
`

/* Keeps each input glued to the dropdown chevron that overlaps it, so a wrap never separates them. */
const ControlGroup = styled.div`
  display: flex;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* The input carries a desktop px floor that would otherwise stick out of the stacked column. */
    > *:first-child {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
`

export default function ExtLogUpload({ charger }: { charger: ChargerType }) {
  const { t } = useTranslation()

  const [extPath, setExtPath] = useState<string>('')
  const [extFilter, setExtFilter] = useState<string>('')
  const [extFilesOnPath, setExtFilesOnPath] = useState<string[]>([])
  const [selectedExtFilesOnPath, setSelectedExtFilesOnPath] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const numberOfTimesFileNotFoundOnServer = useRef<number>(0)

  async function getFilesFromChargerAtPath() {
    try {
      const response = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 86, "path": extPath, "filter": extFilter })
      })
      setExtFilesOnPath((await response.json()).fnames.sort())
    } catch (e) { console.log(e, 'Charger probably offline') }
  }

  function selectFile(file: string) {
    if (!selectedExtFilesOnPath.includes(file)) {
      setSelectedExtFilesOnPath([...selectedExtFilesOnPath, file])
    }
  }

  function unSelectFile(file: string) {
    if (selectedExtFilesOnPath.includes(file)) {
      const allFilesWithoutFile = []
      for (const selectedFile of selectedExtFilesOnPath) {
        if (selectedFile != file) {
          allFilesWithoutFile.push(selectedFile)
        }
      }
      setSelectedExtFilesOnPath(allFilesWithoutFile)
    }
  }

  async function downloadFiles() {
    if (selectedExtFilesOnPath.length > 0) {
      const packFilesResponse = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 88, "path": extPath, "fnames": selectedExtFilesOnPath })
      })
      const zipName = (await packFilesResponse.json()).outname

      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 90, "fname": zipName, "del": true })
      })

      setIsLoading(true)
      downloadFileFromServer(zipName.split('/')[1])
    }
  }

  async function downloadFileFromServer(zip: string) {
    const uploadedFilesResponse = await Comm(`/api-log/charger/extra/loglist?charger_id=${charger.chargerId}`, {})
    const uploadedFilesResult = await uploadedFilesResponse.json()

    let foundTheFile = false

    for (const log of uploadedFilesResult) {
      if (log.logTitle == zip) {

        foundTheFile = true

        const uploadedFilesResponse = await Comm(`/api-log/charger/extra/log?object_id=${log.id}`, {})
        const file = await uploadedFilesResponse.blob()

        const downloadAnchorNode = document.createElement('a')
        downloadAnchorNode.setAttribute("href", window.URL.createObjectURL(file))
        downloadAnchorNode.setAttribute("download", log.logTitle)
        document.body.appendChild(downloadAnchorNode) // required for firefox
        downloadAnchorNode.click()
        downloadAnchorNode.remove()
        setIsLoading(false)
      }
    }

    if (!foundTheFile) {
      if (numberOfTimesFileNotFoundOnServer.current < 10) {
        numberOfTimesFileNotFoundOnServer.current++
        setTimeout(() => downloadFileFromServer(zip), 1000)
      } else {
        setIsLoading(false)
        alert("Unknown error, zip file not found on server")
      }
    }
  }

  return (
    <>
      <WhiteSpace height={'18px'} />
      <PageTitle label={t('charger_extLogUpload')} />
      <WhiteSpace height={'12px'} />

      <FormColumn>
      <ControlsRow>
        <ControlGroup>
          <InputWithLabel label={'Path'} width='max(15vw, 192px)' value={extPath} onInput={(e) => extFilesOnPath.length == 0 && setExtPath(e)} />
          <div style={{marginLeft: '-40px', marginTop: '5px'}}>
            <Dropdown title={{ label: "", icon: "general.downArrow" }} options={[{ label: "log" }, { label: "log/journal" }, { label: "log/ocpp" }, { label: "log/psmaster" }, { label: "log/psclient" }, { label: "log/mid" }, { label: "log/dlm_ipm" }, { label: "/var/log" }]} handleChoose={(value) => { setExtPath(value) }} />
          </div>
        </ControlGroup>
        <ControlGroup>
          <InputWithLabel label={'Filter'} width='max(15vw, 192px)' value={extFilter} onInput={(e) => extFilesOnPath.length == 0 && setExtFilter(e)} />
          <div style={{marginLeft: '-40px', marginTop: '5px'}}>
            <Dropdown title={{ label: "", icon: "general.downArrow" }} options={[{ label: "ocpp" }, { label: "daemon" }]} handleChoose={(value) => { setExtFilter(value) }} />
          </div>
        </ControlGroup>
        <Button label='Reset' width='max(4vw, 88px)' onclick={() => {
          setExtPath('')
          setExtFilter('')
          setExtFilesOnPath([])
          setSelectedExtFilesOnPath([])
          setIsLoading(false)
          numberOfTimesFileNotFoundOnServer.current = 0
        }} />
      </ControlsRow>
      {extFilesOnPath.length == 0 && (
        <>
          <WhiteSpace height="8px" />
          <Button label="Get files" width='min(max(30%, 288px), 100%)' onclick={getFilesFromChargerAtPath} />
        </>
      )}
      </FormColumn>
      <WhiteSpace height="8px" />
      {extFilesOnPath.length != 0 && (
        <div style={{ maxHeight: '30dvh', width: 'min(max(30%, 288px), 100%)', overflowY: 'scroll', border: '1px solid #E4E6EF', borderRadius: '8px' }}>
          {extFilesOnPath && extFilesOnPath.map(file => (
            <React.Fragment key={file}>
              {selectedExtFilesOnPath.includes(file) ? (
                <div style={{ cursor: 'pointer', display: 'flex' }}>
                  <WhiteSpace width="8px" />
                  <Icon path="general.checkboxFull" width="16px" height="16px" margintop="14px" />
                  <WhiteSpace width="8px" />
                  <p style={{ fontWeight: '600', lineHeight: '10px' }} onClick={() => unSelectFile(file)}>{file}</p>
                </div>
              ) : (
                <div style={{ cursor: 'pointer', display: 'flex' }}>
                  <WhiteSpace width="8px" />
                  <Icon path="general.checkboxEmpty" width="16px" height="16px" margintop="14px" />
                  <WhiteSpace width="12px" />
                  <p style={{ lineHeight: '10px' }} onClick={() => selectFile(file)}>{file}</p>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      <WhiteSpace height="8px" />
      {selectedExtFilesOnPath.length > 0 && (
        <>
          {!isLoading ? (
            <Button label='Download selected files' width='min(max(30%, 288px), 100%)' onclick={() => { numberOfTimesFileNotFoundOnServer.current = 0; downloadFiles() }} />
          ) : (
            <LoaderDots />
          )}
        </>
      )}
    </>
  )
}